export type SoundName =
  | 'moveSelf'
  | 'moveOpponent'
  | 'capture'
  | 'castle'
  | 'check'
  | 'promote'
  | 'gameStart'
  | 'gameEnd'
  | 'illegal'
  | 'premove'
  | 'lowTime'
  | 'notify'

/**
 * Sounds are synthesised into buffers at startup rather than loaded as files.
 *
 * Two reasons. Latency: an `<audio>` element on mobile Safari is late enough to
 * break the feel of a fast game, whereas a pre-decoded `AudioBuffer` fires
 * immediately. And licensing: lichess's default sound set is listed in their
 * COPYING as a non-free exception with no stated licence, so it cannot simply
 * be vendored — and nothing here needs to be fetched, hosted or attributed.
 *
 * The pieces are modelled rather than beeped: a real piece on a real board is a
 * noise transient followed by a few inharmonic wooden resonances decaying at
 * different rates. That is what `knock` builds.
 */
const SAMPLE_RATE = 44_100

/** Deterministic white noise, so every render of a sound is identical. */
const noise = (() => {
  let state = 0x2f6e2b1
  return (): number => {
    state ^= state << 13
    state ^= state >>> 17
    state ^= state << 5
    return ((state >>> 0) / 0xffffffff) * 2 - 1
  }
})()

interface Mode {
  /** Multiple of the fundamental. Deliberately inharmonic — wood is not a string. */
  readonly ratio: number
  readonly gain: number
  /** Higher modes must die faster or the knock rings like a bell. */
  readonly decay: number
}

const WOOD_MODES: readonly Mode[] = [
  { ratio: 1, gain: 1, decay: 26 },
  { ratio: 2.76, gain: 0.42, decay: 42 },
  { ratio: 5.4, gain: 0.19, decay: 68 },
  { ratio: 8.93, gain: 0.09, decay: 105 },
]

interface Knock {
  readonly frequency: number
  readonly level: number
  /** Loudness of the initial contact noise, before the body rings. */
  readonly transient: number
  readonly duration: number
  /** Fundamental slides down slightly as the contact settles. */
  readonly droop?: number
}

const renderKnock = (samples: Float32Array, offset: number, knock: Knock): void => {
  const count = Math.min(Math.floor(knock.duration * SAMPLE_RATE), samples.length - offset)
  const droop = knock.droop ?? 0.12
  const phases = WOOD_MODES.map(() => 0)

  for (let index = 0; index < count; index += 1) {
    const time = index / SAMPLE_RATE
    const progress = index / count

    // Contact noise: a couple of milliseconds, steeply gated.
    const transient = knock.transient * Math.exp(-320 * time) * noise()

    let body = 0
    for (const [modeIndex, mode] of WOOD_MODES.entries()) {
      const frequency = knock.frequency * mode.ratio * (1 - droop * progress)
      phases[modeIndex] = (phases[modeIndex] as number) + (2 * Math.PI * frequency) / SAMPLE_RATE
      body += Math.sin(phases[modeIndex] as number) * mode.gain * Math.exp(-mode.decay * time)
    }

    const target = offset + index
    samples[target] = (samples[target] as number) + (transient + body * knock.level)
  }
}

interface Tone {
  readonly frequency: number
  readonly start: number
  readonly duration: number
  readonly level: number
  readonly sweepTo?: number
  /** A little second harmonic keeps a pure sine from sounding like a test tone. */
  readonly timbre?: number
}

const renderTone = (samples: Float32Array, tone: Tone): void => {
  const offset = Math.floor(tone.start * SAMPLE_RATE)
  const count = Math.min(Math.floor(tone.duration * SAMPLE_RATE), samples.length - offset)
  const timbre = tone.timbre ?? 0.22
  let phase = 0

  for (let index = 0; index < count; index += 1) {
    const time = index / SAMPLE_RATE
    const progress = index / count
    const frequency = tone.frequency + ((tone.sweepTo ?? tone.frequency) - tone.frequency) * progress
    phase += (2 * Math.PI * frequency) / SAMPLE_RATE

    // Quick attack, long-ish exponential tail, faded out at the very end so no
    // sound ever clicks when its buffer stops.
    const attack = Math.min(1, time / 0.006)
    const envelope = attack * Math.exp(-7 * time) * (1 - progress) ** 0.4

    const target = offset + index
    samples[target] =
      (samples[target] as number) +
      (Math.sin(phase) + Math.sin(phase * 2) * timbre) * tone.level * envelope
  }
}

type Recipe = (samples: Float32Array) => void

const knocks =
  (...items: readonly (Knock & { readonly at: number })[]): Recipe =>
  (samples) => {
    for (const item of items) renderKnock(samples, Math.floor(item.at * SAMPLE_RATE), item)
  }

const tones =
  (...items: readonly Tone[]): Recipe =>
  (samples) => {
    for (const item of items) renderTone(samples, item)
  }

const both =
  (...recipes: readonly Recipe[]): Recipe =>
  (samples) => {
    for (const recipe of recipes) recipe(samples)
  }

interface Definition {
  readonly seconds: number
  readonly render: Recipe
  /** Peak level after normalisation, so the set is balanced against itself. */
  readonly peak: number
}

const RECIPES: Readonly<Record<SoundName, Definition>> = {
  // Your own move is brighter and closer than the opponent's, which is how you
  // can tell them apart without looking.
  moveSelf: {
    seconds: 0.26,
    peak: 0.72,
    render: knocks({ at: 0, frequency: 210, level: 0.85, transient: 0.5, duration: 0.26 }),
  },
  moveOpponent: {
    seconds: 0.3,
    peak: 0.62,
    render: knocks({ at: 0, frequency: 158, level: 0.8, transient: 0.34, duration: 0.3 }),
  },
  // A capture is two contacts a few milliseconds apart: piece lifted, piece set.
  capture: {
    seconds: 0.34,
    peak: 0.92,
    render: knocks(
      { at: 0, frequency: 132, level: 0.95, transient: 0.85, duration: 0.34, droop: 0.2 },
      { at: 0.028, frequency: 196, level: 0.5, transient: 0.4, duration: 0.24 },
    ),
  },
  // Castling is genuinely two pieces, so it gets two clearly separated knocks.
  castle: {
    seconds: 0.36,
    peak: 0.78,
    render: knocks(
      { at: 0, frequency: 190, level: 0.75, transient: 0.42, duration: 0.22 },
      { at: 0.085, frequency: 150, level: 0.8, transient: 0.46, duration: 0.26 },
    ),
  },
  check: {
    seconds: 0.42,
    peak: 0.8,
    render: both(
      knocks({ at: 0, frequency: 220, level: 0.5, transient: 0.4, duration: 0.2 }),
      tones(
        { frequency: 784, start: 0.02, duration: 0.13, level: 0.34 },
        { frequency: 1_046, start: 0.1, duration: 0.24, level: 0.32 },
      ),
    ),
  },
  promote: {
    seconds: 0.5,
    peak: 0.8,
    render: tones(
      { frequency: 523, start: 0, duration: 0.11, level: 0.3 },
      { frequency: 659, start: 0.075, duration: 0.11, level: 0.3 },
      { frequency: 880, start: 0.15, duration: 0.28, level: 0.34 },
    ),
  },
  gameStart: {
    seconds: 0.55,
    peak: 0.72,
    render: tones(
      { frequency: 392, start: 0, duration: 0.13, level: 0.28 },
      { frequency: 523, start: 0.1, duration: 0.13, level: 0.28 },
      { frequency: 659, start: 0.2, duration: 0.3, level: 0.3 },
    ),
  },
  gameEnd: {
    seconds: 0.75,
    peak: 0.78,
    render: tones(
      { frequency: 659, start: 0, duration: 0.16, level: 0.3 },
      { frequency: 523, start: 0.13, duration: 0.17, level: 0.3 },
      { frequency: 392, start: 0.27, duration: 0.42, level: 0.32 },
    ),
  },
  // A dull thud with no ring: the sound of something not happening.
  illegal: {
    seconds: 0.22,
    peak: 0.6,
    render: knocks({ at: 0, frequency: 96, level: 0.7, transient: 0.5, duration: 0.22, droop: 0.45 }),
  },
  premove: {
    seconds: 0.1,
    peak: 0.34,
    render: tones({ frequency: 1_180, start: 0, duration: 0.06, level: 0.22, timbre: 0.05 }),
  },
  lowTime: {
    seconds: 0.5,
    peak: 0.85,
    render: tones(
      { frequency: 880, start: 0, duration: 0.11, level: 0.34, timbre: 0.1 },
      { frequency: 880, start: 0.16, duration: 0.11, level: 0.34, timbre: 0.1 },
      { frequency: 880, start: 0.32, duration: 0.16, level: 0.34, timbre: 0.1 },
    ),
  },
  notify: {
    seconds: 0.34,
    peak: 0.55,
    render: tones(
      { frequency: 880, start: 0, duration: 0.1, level: 0.24 },
      { frequency: 1_170, start: 0.08, duration: 0.2, level: 0.24 },
    ),
  },
}

const build = (context: BaseAudioContext, definition: Definition): AudioBuffer => {
  const length = Math.ceil(definition.seconds * SAMPLE_RATE)
  const buffer = context.createBuffer(1, length, SAMPLE_RATE)
  const samples = buffer.getChannelData(0)

  definition.render(samples)

  // Normalise to the declared peak so the set is balanced, then soft-clip.
  let loudest = 0
  for (let index = 0; index < length; index += 1) {
    loudest = Math.max(loudest, Math.abs(samples[index] as number))
  }
  const scale = loudest === 0 ? 0 : definition.peak / loudest
  for (let index = 0; index < length; index += 1) {
    samples[index] = Math.tanh((samples[index] as number) * scale * 1.15)
  }
  return buffer
}

const STORAGE_KEY = 'chess:muted'

let context: AudioContext | null = null
let buffers: Map<SoundName, AudioBuffer> | null = null
const lastPlayedAt = new Map<SoundName, number>()
const listeners = new Set<(muted: boolean) => void>()

let muted = ((): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
})()

/**
 * Browsers refuse to start audio until the user has interacted, so this must be
 * called from a real gesture handler. Calling it repeatedly is harmless, and it
 * doubles as the resume path — iOS suspends the context whenever the tab goes
 * to the background, and without resuming, every sound after you switch apps is
 * silently dropped.
 */
export const unlockAudio = (): void => {
  if (context !== null) {
    if (context.state === 'suspended') void context.resume()
    return
  }
  try {
    const created = new AudioContext()
    context = created
    buffers = new Map(
      (Object.keys(RECIPES) as SoundName[]).map((name) => [name, build(created, RECIPES[name])]),
    )
  } catch {
    context = null
  }
}

/**
 * Sounds within this window of each other collapse to one. A premove firing in
 * the same frame as the opponent's move would otherwise stack two knocks into
 * something that sounds like a glitch.
 */
const DEDUPE_MS = 55

export const playSound = (name: SoundName): void => {
  if (muted) return

  const now = Date.now()
  if (now - (lastPlayedAt.get(name) ?? 0) < DEDUPE_MS) return
  lastPlayedAt.set(name, now)

  unlockAudio()
  if (context === null || buffers === null) return

  const buffer = buffers.get(name)
  if (buffer === undefined) return

  const source = context.createBufferSource()
  source.buffer = buffer
  const gain = context.createGain()
  gain.gain.value = 0.9
  source.connect(gain).connect(context.destination)
  source.start()
}

export const isMuted = (): boolean => muted

export const setMuted = (value: boolean): void => {
  muted = value
  for (const listener of listeners) listener(value)
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
  } catch {
    /* Private browsing; the setting simply will not persist. */
  }
}

export const subscribeToMute = (listener: (muted: boolean) => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Keep the context alive across backgrounding. Without this, iOS suspends it on
 * the way out and every later sound is dropped with no error — the opponent's
 * moves simply go silent until you happen to move yourself.
 */
export const watchAudioLifecycle = (): (() => void) => {
  const resume = (): void => {
    if (context !== null && context.state === 'suspended') void context.resume()
  }
  document.addEventListener('visibilitychange', resume)
  window.addEventListener('focus', resume)
  window.addEventListener('pageshow', resume)
  return () => {
    document.removeEventListener('visibilitychange', resume)
    window.removeEventListener('focus', resume)
    window.removeEventListener('pageshow', resume)
  }
}
